/**
 * Date helpers for calendar and bookings.
 * All calendar keys use local-date strings YYYY-MM-DD to avoid timezone mismatches
 * (e.g. a visit showing up a day early or late).
 */

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize a date from the bookings collection to a local YYYY-MM-DD string.
 * Use this so datesWithBookings matches the Calendar's getDateString format.
 * - Plain "YYYY-MM-DD" from DB is used as-is (calendar date, no TZ).
 * - ISO strings or Timestamps are parsed and formatted in local time.
 */
export function toLocalDateString(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" && YYYY_MM_DD.test(value.trim())) return value.trim();
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    /** Firestore `Timestamp` JSON from Admin / REST (`toJSON`), not a live SDK instance with `toDate`. */
    const secRaw = o.seconds ?? o._seconds;
    if (typeof secRaw === "number" && Number.isFinite(secRaw)) {
      const nsRaw = o.nanoseconds ?? o._nanoseconds;
      const ms = typeof nsRaw === "number" && Number.isFinite(nsRaw) ? secRaw * 1000 + Math.floor(nsRaw / 1e6) : secRaw * 1000;
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) return formatLocalYYYYMMDD(d);
    }
    if ("toDate" in o && typeof (o as { toDate: () => Date }).toDate === "function") {
      const d = (o as { toDate: () => Date }).toDate();
      return formatLocalYYYYMMDD(d);
    }
  }
  const d = new Date(value as string | number | Date);
  if (Number.isNaN(d.getTime())) return null;
  return formatLocalYYYYMMDD(d);
}

/**
 * Format a Date as local YYYY-MM-DD (no timezone shift).
 */
function formatLocalYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Parse a YYYY-MM-DD string as local date (noon local to avoid DST edge cases).
 * Use when comparing or creating Date objects from calendar keys.
 */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}

/**
 * Normalize a date input to a strict YYYY-MM-DD string for saving to the database.
 * Use when capturing from <input type="date"> or any string to avoid timestamp/year bugs.
 * - Accepts string "YYYY-MM-DD" (returns as-is, trimmed).
 * - Accepts string that can be split into [y, m, d]; returns padded YYYY-MM-DD.
 * - Never uses Date objects for the return value so the year cannot shift (e.g. UTC).
 */
export function toStrictYYYYMMDD(value: unknown): string | null {
  if (value == null) return null;
  const s = typeof value === "string" ? value.trim() : String(value);
  if (!s) return null;
  if (YYYY_MM_DD.test(s)) return s;
  const parts = s.split("-");
  if (parts.length >= 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d) && y >= 1000 && y <= 9999 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  return null;
}
